import styled from '@emotion/styled';
import { useMemo, useRef } from 'react';

import { SCOPES } from '../../lib/scopes';
import { ToastManager } from '../../ToastManager';

import { devicesStore } from '../../store/devices/DevicesStore.ts';

import { useI18n } from '../../hooks/useI18nFormatters';
import { useDevicesById } from '../../store/devices/useDevices.ts';
import { useApi } from '../../store/SelectedHomeyStore.hooks.ts';
import { useZonesTree } from '../../store/zones/useZones.ts';

import { scrollbarDark } from '../../theme/elements/scrollbars';
import { su } from '../../theme/functions/su';
import { glyph } from '../../theme/glyph';
import { HomeyImages } from '../../theme/HomeyImages';
import { theme } from '../../theme/theme';

import { GradientButton } from '../../components/buttons/GradientButton';
import { IconButton } from '../../components/buttons/IconButton';
import { Glyph } from '../../components/common/Glyph';
import { DeviceIcon } from '../../components/device/DeviceIcon';
import { Switch } from '../../components/forms/Switch';
import { Dialog } from '../../components/overlay/Dialog';

export function HiddenDevicesDialog(props) {
  const { i18n } = useI18n();
  const { api, scopes } = useApi();

  const devices = useDevicesById();
  const zones = useZonesTree();

  const hasDeviceScope = scopes[SCOPES.DEVICE] === true;

  const groupedDevices = useMemo(() => {
    if (devices.byId == null || zones.tree?.list == null || zones.byId == null) {
      return [];
    }

    const descendants =
      props.selectedZoneId != null
        ? (zones.tree.descendants?.[props.selectedZoneId] ?? new Set())
        : null;

    const devicesByZone = {};

    for (const device of Object.values(devices.byId)) {
      if (device.group != null) continue;

      const zoneId = device.zone;

      if (descendants != null && zoneId !== props.selectedZoneId && !descendants.has(zoneId))
        continue;

      if (devicesByZone[zoneId] == null) {
        devicesByZone[zoneId] = [];
      }
      devicesByZone[zoneId].push(device);
    }

    for (const zoneId of Object.keys(devicesByZone)) {
      devicesByZone[zoneId].sort((a, b) => i18n.collator.compare(a.name, b.name));
    }

    const result = [];
    for (const zoneMeta of zones.tree.list) {
      const zoneDevices = devicesByZone[zoneMeta.id];
      if (zoneDevices == null || zoneDevices.length === 0) continue;

      result.push({
        zoneId: zoneMeta.id,
        zoneName: zones.byId[zoneMeta.id]?.name ?? '',
        zoneIcon: zones.byId[zoneMeta.id]?.icon ?? '',
        devices: zoneDevices,
      });
    }

    return result;
  }, [devices.byId, zones.tree, zones.byId, props.selectedZoneId, i18n.collator]);

  const pendingRef = useRef(new Set());

  function handleChange(device) {
    if (pendingRef.current.has(device.id)) return;

    pendingRef.current.add(device.id);
    devicesStore.updateOneOptimistic(device.id, { hidden: !device.hidden });

    api.devices
      .updateDevice({
        id: device.id,
        device: { hidden: !device.hidden },
      })
      .catch((error) => {
        devicesStore.restoreOne(device.id);
        ToastManager.handleError(error);
      })
      .finally(() => {
        pendingRef.current.delete(device.id);
      });
  }

  return (
    <Dialog onClose={props.onClose}>
      <S.Root>
        <S.Header>
          <S.CloseButton
            url={glyph.core.cross}
            color={theme.color.icon_light}
            size={theme.icon.size_medium}
            onPress={props.onClose}
          />
          <S.Title>{i18n.messageFormatter('devices.hiddenDevices')}</S.Title>
          <S.Subtitle>{i18n.messageFormatter('devices.hiddenDevicesSubtitle')}</S.Subtitle>
        </S.Header>

        <S.Content>
          {groupedDevices.map((group) => (
            <S.ZoneGroup key={group.zoneId}>
              <S.ZoneHeader>
                <S.ZoneIconWrapper>
                  <Glyph
                    size={theme.icon.size_medium}
                    source={HomeyImages.getIconForZone(group.zoneIcon)}
                    color={theme.color.icon_dark}
                  />
                </S.ZoneIconWrapper>
                <S.ZoneName>{group.zoneName}</S.ZoneName>
              </S.ZoneHeader>

              <S.DeviceCard>
                {group.devices.map((device) => (
                  <S.DeviceRow key={device.id}>
                    <S.DeviceInfo>
                      <S.DeviceIconWrapper
                        style={{ '--device-icon-bg': device.color ?? theme.color.line }}
                      >
                        <DeviceIcon
                          iconObj={device.iconObj}
                          iconOverride={device.iconOverride}
                          size="18px"
                          color={device.color ? theme.color.white : theme.color.icon_dark}
                        />
                      </S.DeviceIconWrapper>
                      <S.DeviceName title={device.name}>{device.name}</S.DeviceName>
                    </S.DeviceInfo>

                    <Switch
                      aria-label={`Toggle ${device.nam}`}
                      name={`hidden-${device.id}`}
                      value={device.hidden === true}
                      onChange={() => handleChange(device)}
                      isDisabled={!hasDeviceScope}
                    />
                  </S.DeviceRow>
                ))}
              </S.DeviceCard>
            </S.ZoneGroup>
          ))}
        </S.Content>

        <S.Footer>
          <GradientButton styleWidth="full" styleFlat={true} onPress={props.onClose}>
            <GradientButton.Text>{i18n.messageFormatter('common.done')}</GradientButton.Text>
          </GradientButton>
        </S.Footer>
      </S.Root>
    </Dialog>
  );
}

function S() {}
HiddenDevicesDialog.S = S;

S.Root = styled.div`
  --dialog-width: 512px;
  --dialog-height: 658px;

  display: grid;
  grid-template-rows: auto 1fr auto;
  min-width: var(--dialog-width);
  max-width: var(--dialog-width);
  height: var(--dialog-height);
  background-color: ${theme.color.body};
  border-radius: ${theme.borderRadius.default};
  overflow: hidden;
`;

S.Header = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${su(2)} ${su(5)} ${su(2)};
`;

S.CloseButton = styled(IconButton)`
  position: absolute;
  top: ${su(2)};
  right: ${su(2)};
`;

S.Title = styled.h3`
  text-align: center;
  font-size: ${theme.fontSize.heading2};
  font-weight: ${theme.fontWeight.bold};
  margin-bottom: ${su(0.5)};
`;

S.Subtitle = styled.p`
  text-align: center;
  font-size: ${theme.fontSize.regular};
  color: ${theme.color.text_light};
  margin: 0;
  line-height: 1.4;
`;

S.Content = styled.div`
  ${scrollbarDark};
  overflow-y: auto;
  padding: ${su(0)} ${su(4)} ${su(4)};
`;

S.ZoneGroup = styled.div`
  &:not(:last-child) {
    margin-bottom: ${su(3)};
  }
`;

S.ZoneHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${su(1)};
  margin-bottom: ${su(2)};
`;

S.ZoneIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.default};
  background-color: ${theme.color.mono_050};
  flex-shrink: 0;
`;

S.ZoneName = styled.div`
  font-size: ${theme.fontSize.heading3};
  font-weight: ${theme.fontWeight.bold};
`;

S.DeviceCard = styled.div`
  background-color: ${theme.color.component};
  border-radius: ${theme.borderRadius.default};
  overflow: hidden;
`;

S.DeviceRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${su(1)} ${su(2)};

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    bottom: 0;
    inset-inline-start: calc(${su(2)} + 30px + ${su(1.5)});
    inset-inline-end: ${su(2)};
    border-bottom: 1px solid ${theme.color.line_light};
  }
`;

S.DeviceInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${su(1.5)};
  min-width: 0;
  flex: 1 1 auto;
`;

S.DeviceIconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 30px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: var(--device-icon-bg);
`;

S.DeviceName = styled.span`
  font-size: ${theme.fontSize.regular};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

S.Footer = styled.div`
  padding: ${su(2)};
  background-color: ${theme.color.component};
  border-top: 1px solid ${theme.color.line};
`;
